import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api-client";
import {
  Send,
  Loader2,
  Bot,
  User,
  CheckCircle,
  Layers,
  Calendar,
  Flag,
} from "lucide-react";
import type { ProjectTask } from "@/types/api";

interface TaskWithInterpretation {
  task: ProjectTask & { phase?: { id: string; name: string } | null };
  aiInterpretation?: { workArea: string | null; assignee: string | null };
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  task?: ProjectTask & { phase?: { id: string; name: string } | null };
  aiInterpretation?: { workArea: string | null; assignee: string | null };
  tasks?: TaskWithInterpretation[];
  count?: number;
}

interface TaskChatProps {
  projectId: string;
  onTaskCreated: () => void;
}

export const TaskChat = ({ projectId, onTaskCreated }: TaskChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Describe a task in natural language and I'll create it in the right work area. For example:\n\n• \"Crear endpoint de login con JWT y bcrypt\"\n• \"Diseñar la pantalla de dashboard, asignar a David, urgente\"\n• \"Test de integración para el módulo de pagos, para el viernes\"",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const { data } = await api.post(`/projects/${projectId}/chat-task`, {
        message: text,
      });

      const count = data.count || 1;
      const assistantMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: count > 1 ? `${count} tasks created!` : `Task created!`,
        task: data.task,
        aiInterpretation: data.aiInterpretation,
        tasks: data.tasks,
        count,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      onTaskCreated();
    } catch (error) {
      const errMsg =
        error instanceof Error ? error.message : "Failed to create task";
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: `Error: ${errMsg}. Try rephrasing.`,
        },
      ]);
      toast({
        title: "Error",
        description: errMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "URGENT":
        return "bg-red-500 text-white";
      case "HIGH":
        return "bg-orange-500 text-white";
      case "MEDIUM":
        return "bg-yellow-500 text-white";
      default:
        return "bg-green-500 text-white";
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 p-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            {msg.role === "assistant" && (
              <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="h-4 w-4" />
              </div>
            )}

            <div
              className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted"
              }`}
            >
              {(msg.tasks || msg.task) ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 text-emerald-600 font-medium">
                    <CheckCircle className="h-3.5 w-3.5" />
                    {(msg.count || 1) > 1 ? `${msg.count} tasks created` : 'Task created'}
                  </div>
                  {(msg.tasks || [{ task: msg.task!, aiInterpretation: msg.aiInterpretation }]).map((item, idx) => (
                    <div key={idx} className={`space-y-1.5 ${idx > 0 ? 'border-t pt-2' : ''}`}>
                      <div className="font-medium text-sm">{item.task.title}</div>
                      {item.task.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {item.task.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1.5">
                        {item.task.phase && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Layers className="h-2.5 w-2.5" />
                            {item.task.phase.name}
                          </Badge>
                        )}
                        <Badge className={`text-xs ${getPriorityColor(item.task.priority)}`}>
                          <Flag className="h-2.5 w-2.5 mr-0.5" />
                          {item.task.priority}
                        </Badge>
                        {item.aiInterpretation?.assignee && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <User className="h-2.5 w-2.5" />
                            {item.aiInterpretation.assignee}
                          </Badge>
                        )}
                        {item.task.dueDate && (
                          <Badge variant="secondary" className="text-xs gap-1">
                            <Calendar className="h-2.5 w-2.5" />
                            {new Date(item.task.dueDate).toLocaleDateString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="whitespace-pre-line">{msg.content}</p>
              )}
            </div>

            {msg.role === "user" && (
              <div className="h-7 w-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                <User className="h-4 w-4" />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-2.5">
            <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Bot className="h-4 w-4" />
            </div>
            <div className="bg-muted rounded-lg px-3.5 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3 flex gap-2">
        <Input
          ref={inputRef}
          placeholder="Describe a task..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
          disabled={isLoading}
          className="h-9"
        />
        <Button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          size="sm"
          className="h-9 px-3"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};
